/**
 * Nutzung — wann und wofür Web-App und Apps benutzt werden (HM-168, HM-174,
 * ADR 0046).
 *
 * **Nur für den Plattform-Admin**, wie „System"; der Schutz liegt beim Server.
 *
 * **Fünf Reiter statt einer langen Seite** (HM-174): Überblick, Zeiten,
 * Ansichten, Funktionen, Plattformen. Der Überblick beantwortet die Frage, mit
 * der man die Seite öffnet — wie viele sind da, mehr oder weniger als vorher,
 * und was benutzen sie am meisten; jede Liste steht vollständig einen Reiter
 * weiter. Reiter, Zeitraum, Plattform und das Raster des Verlaufs stehen in der
 * Adresse, wie jede Einstellung einer Ansicht in dieser App.
 *
 * **Die Seite zeigt, was, nicht wer.** Es gibt keine Zeile je Nutzer und keine
 * Zahl „Nutzer im Zeitraum": Die Kennung wechselt jeden Tag, und wer an sieben
 * Tagen kam, ist sieben **Nutzertage**. Deshalb fehlen auch die üblichen
 * Kennzahlen über Tage hinweg — Wochen- und Monatsnutzer, Wiederkehr,
 * Bindung —; die Woche zeigt das **Tagesmittel** ihrer Tage. Der eingeklappte
 * Absatz im Überblick sagt das, weil sich die Frage jedem stellt.
 */
import { Button, Card, EmptyState, MetricCard, SegmentedControl, icons } from "@hausmeter/ui";
import { Chart, type ChartSeries } from "@hausmeter/ui/chart";
import type { ApiComponents } from "@hausmeter/sdk";
import { useState, type CSSProperties, type ReactNode } from "react";
import { Disclosure } from "../components/disclosure.js";
import { RouteTabs } from "../components/route-tabs.js";
import { formatDate, formatTimeAgo } from "../lib/format.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import { useUsageReport } from "../lib/queries.js";
import { navigate, USAGE_DAYS, type Route, type UsageDays, type UsagePlatformFilter, type UsageTab } from "../lib/router.js";
import {
  activeUserPoints,
  busiestSlots,
  busiestWeekday,
  changeAgainst,
  entryTitle,
  formatAverage,
  formatChange,
  formatCount,
  formatShare,
  heatLevel,
  heatmapMax,
  hourSlotLabel,
  PLATFORM_LABELS,
  platformSplit,
  weekdayShort,
  WEEKDAY_LONG,
  weekPoints,
  type HeatLevel,
} from "../lib/usage-report.js";

type UsageRoute = Extract<Route, { kind: "usage" }>;
type Report = ApiComponents["schemas"]["AdminUsageReport"];
type Entry = ApiComponents["schemas"]["UsageEntry"];

/** So viele Zeilen je Liste, bevor „alle zeigen" nötig wird. */
const COLLAPSED_ROWS = 15;
/** So viele Einträge je Liste im Überblick — der Rest steht einen Reiter weiter. */
const TOP_ROWS = 5;

const TAB_LABELS: Record<UsageTab, string> = {
  overview: "Überblick",
  times: "Zeiten",
  screens: "Ansichten",
  actions: "Funktionen",
  platforms: "Plattformen",
};

const tiles: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
  gap: "var(--sp-3)",
};

const split: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
  gap: "var(--sp-5)",
};

export function UsageScreen({ route }: { readonly route: UsageRoute }) {
  const report = useUsageReport(route.days, route.platform);
  const set = (change: Partial<Pick<UsageRoute, "days" | "platform" | "grain">>): void =>
    navigate({ ...route, ...change });

  return (
    <>
      <Card
        title="Nutzung von Web-App und Apps"
        subtitle={
          report.data?.computedAt != null
            ? `Gerechnet ${formatTimeAgo(report.data.computedAt)} · Tage und Stunden in ${report.data.timeZone}`
            : "Wann und wofür die Anwendung benutzt wird — ohne Bezug zu einzelnen Nutzern"
        }
      >
        <div style={{ display: "flex", gap: "var(--sp-3)", flexWrap: "wrap" }}>
          <SegmentedControl
            label="Zeitraum"
            size="sm"
            value={String(route.days)}
            onChange={(value) => set({ days: Number(value) as UsageDays })}
            options={USAGE_DAYS.map((days) => ({ value: String(days), label: days === 365 ? "1 Jahr" : `${days} Tage` }))}
          />
          <SegmentedControl
            label="Plattform"
            size="sm"
            value={route.platform ?? "all"}
            onChange={(value) => set({ platform: value === "all" ? null : (value as UsagePlatformFilter) })}
            options={[
              { value: "all", label: "Alle" },
              ...(Object.keys(PLATFORM_LABELS) as UsagePlatformFilter[]).map((platform) => ({
                value: platform,
                label: PLATFORM_LABELS[platform],
              })),
            ]}
          />
        </div>
        {report.isPending && <p style={{ ...mutedText, marginTop: 12 }}>Wird geladen …</p>}
        {report.isError && (
          <p style={{ ...mutedText, marginTop: 12, color: "var(--hm-danger)" }}>{report.error.message}</p>
        )}
      </Card>

      <RouteTabs
        label="Ansichten der Nutzungsstatistik"
        current={route.tab}
        tabs={(Object.keys(TAB_LABELS) as UsageTab[]).map((tab) => ({
          key: tab,
          label: TAB_LABELS[tab],
          route: { ...route, tab },
        }))}
      />

      {report.data && report.data.collectingSince === null && (
        <Card>
          <EmptyState icon={<icons.IconActivity size={22} />} title="Noch keine Nutzung gezählt">
            Die Zahlen entstehen, sobald jemand Web-App oder App benutzt; der Worker rechnet sie alle
            Viertelstunde zu Tageszahlen.
          </EmptyState>
        </Card>
      )}

      {report.data && report.data.collectingSince !== null && (
        <>
          {route.tab === "overview" && <OverviewTab report={report.data} route={route} onGrain={(grain) => set({ grain })} />}
          {route.tab === "times" && <TimesTab report={report.data} />}
          {route.tab === "screens" && (
            <EntryCard
              title="Ansichten"
              subtitle="Wie oft eine Seite geöffnet wurde und an welchem Anteil der Nutzertage"
              entries={report.data.screens}
              unit="Aufrufe"
              empty="Im Zeitraum wurde keine Ansicht geöffnet."
            />
          )}
          {route.tab === "actions" && (
            <EntryCard
              title="Funktionen"
              subtitle="Was an der API geändert und in der Oberfläche umgestellt wurde"
              entries={report.data.actions}
              unit="Ausführungen"
              empty="Im Zeitraum wurde keine Funktion ausgeführt."
            />
          )}
          {route.tab === "platforms" && <PlatformsTab report={report.data} />}
        </>
      )}
    </>
  );
}

/* ---- Überblick ------------------------------------------------------------ */

function OverviewTab({
  report,
  route,
  onGrain,
}: {
  readonly report: Report;
  readonly route: UsageRoute;
  readonly onGrain: (grain: UsageRoute["grain"]) => void;
}) {
  const previous = report.previous;
  const change = changeAgainst(report.averageActiveUsers, previous?.averageActiveUsers);
  const viewsChange = changeAgainst(report.screenViews, previous?.screenViews);
  const weekly = route.grain === "week";

  const series: ChartSeries = weekly
    ? { id: "active-users-week", label: "Nutzer je Tag, Wochenmittel", mark: "bars", points: weekPoints(report) }
    : { id: "active-users", label: "Nutzer am Tag", mark: "bars", points: activeUserPoints(report) };
  const format = {
    axis: (value: number) => formatCount(value),
    value: (value: number) => `${formatAverage(value) ?? "–"} Nutzer${weekly ? " je Tag" : ""}`,
  };
  /* Die Achse beginnt am Montag der ersten Woche — sonst ragte ihr Balken links hinaus. */
  const from = weekly && report.weeks[0] !== undefined ? report.weeks[0].startsAt : report.windowStart;

  return (
    <>
      <div style={tiles}>
        <MetricCard
          icon={<icons.IconUsers size={16} />}
          label="Nutzer je Tag"
          value={formatAverage(report.averageActiveUsers)}
          caption={
            previous === null
              ? "im Mittel der gezählten Tage"
              : `vorher ${formatAverage(previous.averageActiveUsers) ?? "–"} je Tag`
          }
          {...deltaProps(change)}
        />
        <MetricCard
          icon={<icons.IconCalendar size={16} />}
          label="Stärkster Tag"
          value={report.peak === null ? null : formatCount(report.peak.activeUsers)}
          caption={report.peak === null ? "noch kein Tag mit Nutzung" : formatDate(report.peak.date)}
        />
        <MetricCard
          icon={<icons.IconTemplate size={16} />}
          label="Ansichten je Nutzertag"
          value={formatAverage(report.viewsPerUserDay)}
          caption={`${formatCount(report.screenViews)} Ansichten an ${formatCount(report.userDays)} Nutzertagen`}
          {...deltaProps(viewsChange)}
        />
        <MetricCard
          icon={<icons.IconGear size={16} />}
          label="Funktionen je Nutzertag"
          value={formatAverage(report.actionsPerUserDay)}
          caption={`${formatCount(report.actionCount)} Änderungen und Handlungen`}
        />
      </div>

      <Card
        title="Nutzer"
        subtitle={
          weekly
            ? "Tagesmittel je Kalenderwoche — Teilwochen am Rand blasser"
            : `Je Tag, ${formatDate(report.from)} bis ${formatDate(report.to)}`
        }
        action={
          <SegmentedControl
            label="Raster"
            size="sm"
            value={route.grain}
            onChange={(value) => onGrain(value as UsageRoute["grain"])}
            options={[
              { value: "day", label: "Tage" },
              { value: "week", label: "Wochen" },
            ]}
          />
        }
      >
        <Chart
          series={[series]}
          from={new Date(from).getTime()}
          to={new Date(report.windowEnd).getTime()}
          bucket={weekly ? "1w" : "1d"}
          format={format}
          timeZone={report.timeZone}
          height={220}
          label={`${series.label} vom ${formatDate(report.from)} bis ${formatDate(report.to)}`}
        />
        {report.collectingSince !== null && report.collectingSince > report.from && (
          <p style={{ ...mutedText, marginTop: 8 }}>
            Gezählt wird seit {formatDate(report.collectingSince)}; davor ist der Verlauf leer, nicht null.
          </p>
        )}
        {previous === null && (
          <p style={{ ...mutedText, marginTop: 8 }}>
            Ein Vergleich mit dem Zeitraum davor erscheint, sobald der ganz gezählt ist.
          </p>
        )}
      </Card>

      <Card title="Am meisten benutzt" subtitle="Anteil der Nutzertage, an denen es vorkam">
        <div style={split}>
          <TopList
            title="Ansichten"
            entries={report.screens}
            more={{ ...route, tab: "screens" }}
            empty="Keine Ansicht geöffnet."
          />
          <TopList
            title="Funktionen"
            entries={report.actions}
            more={{ ...route, tab: "actions" }}
            empty="Keine Funktion ausgeführt."
          />
        </div>
      </Card>

      <CountedNote report={report} />
    </>
  );
}

/**
 * Die Veränderung gegenüber dem Vorzeitraum — **grau**. Mehr Nutzung ist für den
 * Betreiber meist gut, aber die Kachel weiß das nicht: Nach einer Störung kommen
 * mehr Ansichten, weil Leute nachsehen. Der Pfeil sagt, wohin es ging.
 */
function deltaProps(change: number | null): { delta?: string; deltaDirection?: "up" | "down"; deltaTone?: "neutral" } {
  if (change === null) return {};
  return { delta: formatChange(change), deltaDirection: change < 0 ? "down" : "up", deltaTone: "neutral" };
}

function TopList({
  title,
  entries,
  more,
  empty,
}: {
  readonly title: string;
  readonly entries: readonly Entry[];
  readonly more: Route;
  readonly empty: string;
}) {
  return (
    <div>
      <h4 style={{ margin: "0 0 6px", fontSize: 13 }}>{title}</h4>
      {entries.length === 0 && <p style={mutedText}>{empty}</p>}
      <ul style={plainList}>
        {/* Nach dem Anteil, den die Zeile zeigt — nicht nach Aufrufen: Eine Ansicht,
            die wenige zwanzigmal am Tag öffnen, ist nicht die verbreitetste. */}
        {[...entries]
          .sort((a, b) => b.userDays - a.userDays || b.events - a.events)
          .slice(0, TOP_ROWS)
          .map((entry) => (
          <li key={`${entry.kind}-${entry.name}`} style={{ ...listRow, gap: 10 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflowWrap: "anywhere" }}>{entryTitle(entry)}</span>
            <ShareBar share={entry.userDayShare} />
            <span style={{ ...mutedText, fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 40, textAlign: "right" }}>
              {formatShare(entry.userDayShare) ?? "–"}
            </span>
          </li>
          ))}
      </ul>
      {entries.length > TOP_ROWS && (
        <div style={{ marginTop: 8 }}>
          <Button size="sm" variant="ghost" onClick={() => navigate(more)}>
            Alle {formatCount(entries.length)} {title}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Ein Anteilsbalken — Wegweisung, keine Größe: neutral und nicht in einer
 * Datenfarbe (ADR 0013). `grow` lässt ihn den freien Platz einer Zeile füllen,
 * statt mit fester Breite den Text daneben auf einem Telefon umbrechen zu lassen.
 */
function ShareBar({ share, width = 72, grow = false }: { readonly share: number | null; readonly width?: number; readonly grow?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        ...(grow ? { flex: "1 1 40px", maxWidth: width } : { width, flexShrink: 0 }),
        height: 4,
        borderRadius: 2,
        background: "var(--surface-sunken)",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          display: "block",
          height: "100%",
          width: `${share === null || share === 0 ? 0 : Math.max(3, share * 100)}%`,
          background: "var(--text-muted)",
        }}
      />
    </span>
  );
}

function CountedNote({ report }: { readonly report: Report }) {
  const [open, setOpen] = useState(false);
  return (
    <Card padding="none">
      <Disclosure
        id="usage-counted"
        title="Was hier gezählt wird"
        summary="ohne Kennung über Tage hinweg"
        open={open}
        onToggle={() => setOpen((value) => !value)}
      />
      {open && (
        <div id="usage-counted" style={{ padding: "0 var(--sp-5) var(--sp-5)" }}>
          <p style={mutedText}>
            Gezählt werden geöffnete Ansichten und ausgeführte Funktionen angemeldeter Nutzer in Web-App und
            App, je Tag, Stunde und Plattform. <strong>Wer etwas getan hat, steht nirgends:</strong> Jeder
            Nutzer trägt nur eine Kennung, die jeden Tag neu entsteht. Deshalb gibt es Nutzer je Tag und
            Nutzertage, aber keine Nutzer je Woche oder Monat, keine Wiederkehr und keine Bindung — eine Woche
            zeigt das Mittel ihrer Tage. Einzelne Ereignisse werden {report.eventRetentionDays} Tage
            aufbewahrt, die Tageszahlen danach ohne jede Kennung. Wer die Statistik im Profil abgewählt hat,
            und der lokale Betrieb der App ohne Konto fehlen hier.
          </p>
        </div>
      )}
    </Card>
  );
}

/* ---- Zeiten --------------------------------------------------------------- */

function TimesTab({ report }: { readonly report: Report }) {
  const heatmap = report.heatmap;
  const top = busiestSlots(heatmap);
  const weekday = busiestWeekday(report.weekdays);
  const maxWeekday = report.weekdays.reduce((max, day) => Math.max(max, day.averageActiveUsers ?? 0), 0);

  return (
    <>
      <div style={tiles}>
        <MetricCard
          icon={<icons.IconActivity size={16} />}
          label="Stärkste Stunde"
          value={top[0] === undefined ? null : hourSlotLabel(top[0].weekday, top[0].hour)}
          caption={top[0] === undefined ? "noch keine Stunde gezählt" : `im Mittel ${formatAverage(top[0].averageActiveUsers)} Nutzer`}
        />
        <MetricCard
          icon={<icons.IconCalendar size={16} />}
          label="Stärkster Wochentag"
          value={weekday === null ? null : WEEKDAY_LONG[weekday.weekday - 1] ?? null}
          caption={weekday === null ? "noch kein Tag mit Nutzung" : `im Mittel ${formatAverage(weekday.averageActiveUsers)} Nutzer`}
        />
      </div>

      <Card
        title="Wochentag und Uhrzeit"
        subtitle={
          heatmap.since === null
            ? "Nutzer im Mittel je Stunde"
            : `Nutzer im Mittel je Stunde, über ${formatCount(heatmap.days)} Tage seit ${formatDate(heatmap.since)}`
        }
      >
        {heatmap.since === null ? (
          <EmptyState icon={<icons.IconCalendar size={22} />} title="Noch keine Stunden gezählt">
            Die Stunde wird seit dieser Fassung mitgezählt; die ersten Felder füllen sich mit der nächsten
            Rechnung des Workers.
          </EmptyState>
        ) : (
          <Heatmap heatmap={heatmap} />
        )}
        {heatmap.since !== null && report.collectingSince !== null && heatmap.since > report.from && (
          <p style={{ ...mutedText, marginTop: 10 }}>
            Stunden gibt es seit {formatDate(heatmap.since)}; die Tage davor gehen hier nicht ein.
          </p>
        )}
      </Card>

      <Card title="Wochentage" subtitle="Nutzer je Tag im Mittel, je Wochentag über die gezählten Tage des Zeitraums">
        <ul style={plainList}>
          {report.weekdays.map((day) => (
            <li key={day.weekday} style={{ ...listRow, gap: 12 }}>
              <span style={{ ...rowLabel, minWidth: 0, flex: "0 0 96px" }}>{WEEKDAY_LONG[day.weekday - 1]}</span>
              <ShareBar share={maxWeekday === 0 || day.averageActiveUsers === null ? null : day.averageActiveUsers / maxWeekday} width={240} grow />
              <span
                title={`über ${formatCount(day.days)} ${day.days === 1 ? "Tag" : "Tage"}`}
                style={{ ...mutedText, marginLeft: "auto", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
              >
                {day.averageActiveUsers === null ? "nicht gezählt" : `${formatAverage(day.averageActiveUsers)} Nutzer`}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/**
 * Die Farbe einer Stufe: **ein** Ton, von hell nach kräftig — eine Menge, keine
 * Kategorie. Der Ton ist die Datenpunktfarbe (`--hm-series-datapoint`): Nutzer
 * sind keine Energiegröße, und eine Größenfarbe hier wäre eine Aussage, die
 * niemand gemeint hat (ADR 0013). Gemischt wird mit dem Kartengrund, damit beide
 * Themen von selbst tragen.
 */
const LEVEL_FILL: Record<Exclude<HeatLevel, null>, string> = {
  0: "var(--surface-sunken)",
  1: "color-mix(in oklab, var(--hm-series-datapoint) 22%, var(--surface-card))",
  2: "color-mix(in oklab, var(--hm-series-datapoint) 45%, var(--surface-card))",
  3: "color-mix(in oklab, var(--hm-series-datapoint) 70%, var(--surface-card))",
  4: "var(--hm-series-datapoint)",
};

function cellStyle(level: HeatLevel): CSSProperties {
  return level === null
    ? { background: "transparent", boxShadow: "inset 0 0 0 1px var(--border-subtle)" }
    : { background: LEVEL_FILL[level] };
}

/**
 * Wochentag × Stunde als **Tabelle**: Zeilen sind Wochentage, Spalten Stunden.
 * Eine echte Tabelle, weil eine Sprachausgabe darin Kopf und Zelle zuordnet;
 * jede Zelle trägt ihren Satz als `aria-label`. Die Zeile darüber liest das
 * überfahrene Feld ab — 168 Tabulatorstopps wären keine Bedienung.
 */
function Heatmap({ heatmap }: { readonly heatmap: Report["heatmap"] }) {
  const max = heatmapMax(heatmap);
  const [hovered, setHovered] = useState<Report["heatmap"]["cells"][number] | null>(null);
  const top = busiestSlots(heatmap);
  const describe = (cell: Report["heatmap"]["cells"][number]): string =>
    `${hourSlotLabel(cell.weekday, cell.hour)}: ${
      cell.averageActiveUsers === null ? "nicht gezählt" : `im Mittel ${formatAverage(cell.averageActiveUsers)} Nutzer · ${formatCount(cell.events)} Ereignisse`
    }`;

  return (
    <div>
      <p aria-live="polite" style={{ ...mutedText, minHeight: 20, marginBottom: 8, fontVariantNumeric: "tabular-nums" }}>
        {hovered !== null
          ? describe(hovered)
          : top.length === 0
            ? "Im Zeitraum war zu keiner gezählten Stunde jemand da."
            : `Am stärksten: ${top.map((cell) => hourSlotLabel(cell.weekday, cell.hour)).join(", ")}`}
      </p>
      <table
        style={{ width: "100%", borderCollapse: "separate", borderSpacing: 2, tableLayout: "fixed" }}
        onMouseLeave={() => setHovered(null)}
      >
        <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
          Nutzer im Mittel je Wochentag und Stunde
        </caption>
        <colgroup>
          <col style={{ width: 28 }} />
          {Array.from({ length: 24 }, (_, hour) => (
            <col key={hour} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th scope="col" />
            {Array.from({ length: 24 }, (_, hour) => (
              <th key={hour} scope="col" style={{ ...axisText, textAlign: "left" }}>
                {/* Jede dritte Stunde beschriftet — 24 Zahlen passen auf kein Telefon. */}
                <span aria-hidden>{hour % 3 === 0 ? hour : ""}</span>
                <span style={visuallyHidden}>{hour} Uhr</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
            <tr key={weekday}>
              <th scope="row" style={{ ...axisText, textAlign: "left" }}>
                {weekdayShort(weekday)}
              </th>
              {heatmap.cells
                .filter((cell) => cell.weekday === weekday)
                .map((cell) => {
                  const level = heatLevel(cell.averageActiveUsers, max);
                  const active = hovered?.weekday === cell.weekday && hovered.hour === cell.hour;
                  return (
                    <td
                      key={cell.hour}
                      aria-label={describe(cell)}
                      onMouseEnter={() => setHovered(cell)}
                      style={{
                        ...cellStyle(level),
                        height: 22,
                        padding: 0,
                        borderRadius: 3,
                        outline: active ? "2px solid var(--text-strong)" : "none",
                        outlineOffset: 1,
                      }}
                    />
                  );
                })}
            </tr>
          ))}
        </tbody>
      </table>
      <HeatLegend max={max} />
    </div>
  );
}

function HeatLegend({ max }: { readonly max: number }) {
  const item = (level: HeatLevel, text: ReactNode) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span aria-hidden style={{ ...cellStyle(level), width: 14, height: 14, borderRadius: 3, display: "inline-block" }} />
      {text}
    </span>
  );
  return (
    <div style={{ ...mutedText, display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 12, fontSize: 12 }}>
      {item(null, "nicht gezählt")}
      {item(0, "niemand")}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        wenig
        {([1, 2, 3, 4] as const).map((level) => (
          <span key={level} aria-hidden style={{ ...cellStyle(level), width: 14, height: 14, borderRadius: 3, display: "inline-block" }} />
        ))}
        bis {formatAverage(max)} Nutzer
      </span>
    </div>
  );
}

const axisText: CSSProperties = { fontSize: 11, fontWeight: 500, color: "var(--text-muted)", padding: "0 0 2px", whiteSpace: "nowrap" };
const visuallyHidden: CSSProperties = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" };

/* ---- Plattformen ---------------------------------------------------------- */

function PlatformsTab({ report }: { readonly report: Report }) {
  const maxUserDays = report.platforms.reduce((max, platform) => Math.max(max, platform.userDays), 0);
  return (
    <>
      <Card title="Plattformen" subtitle="Nutzertage und Mittel je Tag — wer Web und App am selben Tag nutzt, zählt in beiden">
        <ul style={plainList}>
          {report.platforms.map((platform) => (
            <li key={platform.platform} style={{ ...listRow, gap: 12 }}>
              <span style={{ ...rowLabel, minWidth: 0, flex: "0 0 96px" }}>{PLATFORM_LABELS[platform.platform]}</span>
              <ShareBar share={maxUserDays === 0 ? null : platform.userDays / maxUserDays} width={160} grow />
              <span style={{ ...mutedText, flex: "1 1 180px", textAlign: "right" }}>
                {platform.userDays === 0
                  ? "keine Nutzung im Zeitraum"
                  : `${formatCount(platform.userDays)} Nutzertage · im Mittel ${formatAverage(platform.averageActiveUsers)} je Tag`}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="App-Fassungen" subtitle="Welche Fassung der App im Zeitraum benutzt wurde — die Web-App hat keine eigene">
        {report.versions.length === 0 ? (
          <p style={mutedText}>Im Zeitraum wurde die App nicht benutzt.</p>
        ) : (
          <ul style={plainList}>
            {report.versions.map((version) => (
              <li key={`${version.platform}-${version.appVersion}`} style={listRow}>
                <span style={rowLabel}>
                  {PLATFORM_LABELS[version.platform]} {version.appVersion}
                </span>
                <span style={{ ...mutedText, flex: 1 }}>
                  {formatCount(version.userDays)} Nutzertage · zuletzt {formatDate(version.lastDate)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

/* ---- Ansichten und Funktionen --------------------------------------------- */

function EntryCard({
  title,
  subtitle,
  entries,
  unit,
  empty,
}: {
  readonly title: string;
  readonly subtitle: string;
  readonly entries: readonly Entry[];
  readonly unit: string;
  readonly empty: string;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? entries : entries.slice(0, COLLAPSED_ROWS);

  return (
    <Card title={title} subtitle={subtitle}>
      {entries.length === 0 && <p style={mutedText}>{empty}</p>}
      <ul style={plainList}>
        {shown.map((entry) => (
          <li key={`${entry.kind}-${entry.name}`} style={{ ...listRow, alignItems: "flex-start" }}>
            <span style={{ display: "flex", flexDirection: "column", flex: "1 1 260px", minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{entryTitle(entry)}</span>
              {entry.kind === "api" && entry.label !== null && (
                <code style={{ ...mutedText, fontSize: 11.5, overflowWrap: "anywhere" }}>{entry.name}</code>
              )}
              <span style={{ ...mutedText, fontSize: 12 }}>{platformSplit(entry.byPlatform)}</span>
            </span>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flex: "0 0 auto", gap: 4 }}>
              <span style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                {formatCount(entry.events)} {unit}
              </span>
              <span style={{ ...mutedText, fontSize: 12 }}>
                an {formatShare(entry.userDayShare) ?? "–"} der Nutzertage
              </span>
              <ShareBar share={entry.userDayShare} width={96} />
            </span>
          </li>
        ))}
      </ul>
      {entries.length > COLLAPSED_ROWS && (
        <div style={{ marginTop: 10 }}>
          <Button size="sm" variant="ghost" onClick={() => setAll((value) => !value)}>
            {all ? "Weniger zeigen" : `Alle ${formatCount(entries.length)} zeigen`}
          </Button>
        </div>
      )}
    </Card>
  );
}
